import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { cacheRows, cachedRows } from '@/utils/chatListCache';
import { isEventLive } from '@/utils/isEventLive';
import { logger } from '@/utils/logger';
import { formatProfileName } from '@/utils/profileHelpers';

/** Matches the bound applied to org event lists in useVolunteersPage. */
export const DIRECT_CHATS_LIMIT = 200;

/**
 * A quiet refetch leaves `loading` alone.
 *
 * The Communication page re-reads its lists whenever the tab or the app comes back to the
 * foreground. Flipping `loading` for that would replace a perfectly good list with a
 * skeleton every time the user tabs back, so a background resync asks for silence.
 */
export type ChatListRefetchOptions = { quiet?: boolean };

export type DirectChatRow = {
  id: string;
  eventId: string;
  eventName: string | null;
  organizationId: string;
  volunteerId: string;
  createdBy: string;
  createdAt: string;
  lastMessageAt: string | null;
  /** The person on the other side, from the viewer's perspective. */
  counterpartId: string;
  counterpartName: string;
  counterpartImage: string | null;
};

type RawChat = {
  id: string;
  event_id: string;
  organization_id: string;
  volunteer_id: string;
  created_by: string;
  created_at: string;
  last_message_at: string | null;
};

type RawEvent = { id: string; name: string | null; end_date: string | null; is_active: boolean | null };
type RawProfile = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  profile_image: string | null;
};

/**
 * Private threads visible to this user, in both directions.
 *
 * RLS decides what comes back — "direct_chats: participants read" admits the volunteer
 * the thread belongs to and any admin of the owning org — so this deliberately does not
 * filter by viewer id. It only needs the viewer id to work out who the counterpart is.
 */
export function useDirectChats(userId: string | null, authLoading = false) {
  const cacheKey = userId ? `direct:${userId}` : null;
  // Seeded rather than filled by an effect: an effect runs after the first paint, which
  // is the blank list we are trying not to show. See utils/chatListCache.
  const [chats, setChats] = useState<DirectChatRow[]>(
    () => cachedRows<DirectChatRow>(cacheKey) ?? []
  );
  const [loading, setLoading] = useState(() => cachedRows(cacheKey) == null);
  const [error, setError] = useState<string | null>(null);
  /** Whose rows are in state, so only a change of viewer clears them. */
  const cacheKeyRef = useRef<string | null>(cacheKey);

  const fetchChats = useCallback(async (options?: ChatListRefetchOptions) => {
    const quiet = options?.quiet === true;
    // Rows for this viewer already in hand: show them and refresh underneath, rather than
    // blanking to a skeleton. On a switch to another account the key misses, and the
    // previous viewer's rows are dropped instead.
    const cached = cachedRows<DirectChatRow>(cacheKey);
    if (cacheKeyRef.current !== cacheKey) {
      cacheKeyRef.current = cacheKey;
      setChats(cached ?? []);
    } else if (cached) {
      setChats(cached);
    }
    if (authLoading) {
      if (!cached) setLoading(true);
      return;
    }
    if (!userId) {
      setChats([]);
      setLoading(false);
      return;
    }

    if (cached) setLoading(false);
    else if (!quiet) setLoading(true);
    setError(null);
    try {
      const { data: chatRows, error: chatError } = await supabase
        .from('direct_chats')
        .select('id, event_id, organization_id, volunteer_id, created_by, created_at, last_message_at')
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .limit(DIRECT_CHATS_LIMIT);

      if (chatError) throw chatError;

      const raw = (chatRows ?? []) as RawChat[];
      if (raw.length === 0) {
        const empty: DirectChatRow[] = [];
        cacheRows(cacheKey, empty);
        setChats(empty);
        return;
      }
      if (raw.length === DIRECT_CHATS_LIMIT) {
        logger.warn('useDirectChats: hit the row cap, older threads are not listed', {
          limit: DIRECT_CHATS_LIMIT,
        });
      }

      const eventIds = [...new Set(raw.map((r) => r.event_id))];
      // The counterpart is the volunteer when you are the org side, and the org when you
      // are the volunteer. Fetch both sides and pick per row.
      const profileIds = [
        ...new Set(raw.flatMap((r) => [r.volunteer_id, r.organization_id])),
      ];

      const [eventsRes, profilesRes] = await Promise.all([
        supabase.from('events').select('id, name, end_date, is_active').in('id', eventIds),
        supabase.from('profiles').select('id, first_name, last_name, profile_image').in('id', profileIds),
      ]);

      if (eventsRes.error) throw eventsRes.error;
      if (profilesRes.error) throw profilesRes.error;

      const eventsById = new Map<string, RawEvent>(
        ((eventsRes.data ?? []) as RawEvent[]).map((e) => [e.id, e])
      );
      const profilesById = new Map<string, RawProfile>(
        ((profilesRes.data ?? []) as RawProfile[]).map((p) => [p.id, p])
      );

      const now = Date.now();
      const rows = raw
        .filter((r) => isEventLive(eventsById.get(r.event_id), now))
        .map((r) => {
          const viewerIsVolunteer = r.volunteer_id === userId;
          const counterpartId = viewerIsVolunteer ? r.organization_id : r.volunteer_id;
          const p = profilesById.get(counterpartId);
          return {
            id: r.id,
            eventId: r.event_id,
            eventName: eventsById.get(r.event_id)?.name ?? null,
            organizationId: r.organization_id,
            volunteerId: r.volunteer_id,
            createdBy: r.created_by,
            createdAt: r.created_at,
            lastMessageAt: r.last_message_at,
            counterpartId,
            counterpartName: formatProfileName(
              p?.first_name ?? undefined,
              p?.last_name ?? undefined,
              viewerIsVolunteer ? 'Organization' : 'Volunteer'
            ),
            counterpartImage: p?.profile_image ?? null,
          };
        });

      // Only a successful read is remembered, so an error leaves the last good rows for
      // the next mount rather than caching a blank list.
      cacheRows(cacheKey, rows);
      setChats(rows);
    } catch (e) {
      logger.error('useDirectChats', e);
      setError(e instanceof Error ? e.message : 'Failed to load direct chats');
      setChats([]);
    } finally {
      setLoading(false);
    }
  }, [userId, authLoading, cacheKey]);

  useEffect(() => {
    void fetchChats();
  }, [fetchChats]);

  return { chats, loading, error, refetch: fetchChats };
}
